import { Download, ExternalLink, File } from "@/icons";
import { RichText } from "@/sections";
import { GetValues, Link } from "@anoki/fse-common";
import clsx from "clsx";
import { saveAs } from "file-saver";
import { ElementType } from "react";
import { ArrowLink } from "../ArrowLink";
import { Button } from "../Button";
import "./CardFile.scss";

export enum CardFileActionTypeEnum {
  DOWNLOAD = "download",
  GOTO = "goto",
  EXTERNAL = "external",
}

export type CardFileActionTypeTypes = GetValues<typeof CardFileActionTypeEnum>;

export type CardFileProps = {
  title: string;
  description?: string;
  issued_at: string;
  issued_at_position?: "top" | "bottom";
  file?: {
    type: string;
    size: string;
  };
  action: {
    type: CardFileActionTypeTypes;
    link: Link;
  };
  asLink?: "a" | ElementType;
  goToLink?: Link;
};

export function CardFile({
  title,
  description,
  issued_at,
  file,
  action,
  asLink = "a",
  goToLink,
}: CardFileProps) {
  const LinkComponent = asLink;

  const handleDownload = async () => {
    try {
      const link = action.link.link;
      const dataUriMatch = link.match(/^data:([^;]+);base64,(.+)$/);

      if (dataUriMatch) {
        const contentType = dataUriMatch[1];
        const base64 = dataUriMatch[2];
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: contentType });
        saveAs(blob, title);
      } else {
        const response = await fetch(link);
        const blob = await response.blob();
        saveAs(blob, title);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  return (
    <div className="card-file shadow rounded h-100 w-100 bg-white">
      <div
        className={clsx("top-container pt-x16 pb-x12", {
          "px-x16": action.type === "download",
          "px-x24 ": action.type !== "download",
        })}
      >
        {action.type === "download" && (
          <div className="icon-wrapper">
            <File height={27} width={27} />
          </div>
        )}
        <div className="content-container">
          <div>
            <h3
              className={clsx(
                "card-file-title text-slate-20 mt-x8",
                "cursor-pointer hover-underline",
              )}
            >
              {action.type === "download" ? (
                title
              ) : (
                <LinkComponent
                  className="link-unstyled"
                  href={action.link.link}
                  target={action.type === "external" ? "_blank" : undefined}
                  rel={
                    action.type === "external"
                      ? "noopener noreferrer"
                      : undefined
                  }
                >
                  {title}
                </LinkComponent>
              )}
            </h3>
            {file && (file.type || file.size) && (
              <p className="file-type text-muted">
                {file.type && file.size
                  ? `${file.type} | ${file.size}`
                  : file.type || file.size}
              </p>
            )}
          </div>
          {description && (
            <div className="description text-slate-28">
              <RichText description={description} />
            </div>
          )}
          {issued_at && (
            <p className="description text-slate-28">{issued_at}</p>
          )}
        </div>
      </div>
      <div
        className={clsx("card-file-footer px-x24", action.type, {
          "pt-x4 pb-x16": action.type === "external",
          "py-x16 ": action.type !== "external",
          "--between": !!goToLink,
        })}
      >
        {action.type === "download" ? (
          <>
            <Button
              className="bg-transparent download-button"
              onClick={handleDownload}
            >
              <Download />
              {action.link.title}
            </Button>
            {goToLink && (
              <ArrowLink href={goToLink.link} asLink={asLink}>
                {goToLink.title}
              </ArrowLink>
            )}
          </>
        ) : action.type === "goto" ? (
          <ArrowLink href={action.link.link} asLink={asLink}>
            {action.link.title}
          </ArrowLink>
        ) : (
          <LinkComponent
            title={action.link.title}
            href={action.link.link}
            src={action.link.link}
            icon={ExternalLink}
            decoration="ui-footer-logo-link"
            transform="none"
            external={true}
            asLink={asLink}
          />
        )}
      </div>
    </div>
  );
}
